import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Circle, X, ArrowRight, Wallet, FileText, Coins } from "lucide-react";
import { api } from "@/lib/api";

const STEPS = [
  {
    key: "add_wallet",
    title: "Add your wallet",
    body: "Set the wallet address where client payments will land. Quill is non-custodial — funds settle directly to you.",
    cta: "Open Settings",
    href: "/settings",
    icon: Wallet,
  },
  {
    key: "send_invoice",
    title: "Send your first invoice",
    body: "Create an invoice for a client. They'll get a hosted page with your name, amount, and a pay-on-chain link.",
    cta: "New invoice",
    href: "/invoices/new",
    icon: FileText,
  },
  {
    key: "get_paid",
    title: "Get paid",
    body: "When a client settles on-chain, the invoice flips to paid and the amount lands in your wallet ledger.",
    cta: "View invoices",
    href: "/invoices",
    icon: Coins,
  },
];

const OnboardingChecklist = ({ onboarding, onRefresh }) => {
  const navigate = useNavigate();
  const [dismissing, setDismissing] = useState(false);

  if (!onboarding || onboarding.dismissed) return null;

  const { steps, complete } = onboarding;
  const done = STEPS.filter((s) => steps[s.key]).length;
  const total = STEPS.length;
  const pct = Math.round((done / total) * 100);
  const activeIdx = STEPS.findIndex((s) => !steps[s.key]);

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/dashboard/onboarding/dismiss");
      onRefresh?.();
    } catch {
      setDismissing(false);
    }
  };

  return (
    <div
      data-testid="onboarding-checklist"
      className="border-b border-[#262626] bg-[#0E0E0E]"
    >
      <div className="px-8 py-6 flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
            Getting started
          </div>
          <h2 className="font-display text-2xl tracking-tight">
            {complete ? "You're all set up." : "Three steps to your first on-chain payout."}
          </h2>
          <p className="text-sm text-zinc-400 mt-1.5 max-w-xl">
            {complete
              ? "You've connected a wallet, sent an invoice, and received a payment. Dismiss this card when you're ready."
              : "Quill works best once your wallet is connected and a client has paid. Knock these out and you're live."}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-mono text-2xl tracking-tight" data-testid="onboarding-progress-text">
              {done}<span className="text-zinc-600">/{total}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">
              {pct}% complete
            </div>
          </div>
          <button
            onClick={dismiss}
            disabled={dismissing}
            data-testid="onboarding-dismiss-btn"
            title="Dismiss"
            className="text-zinc-500 hover:text-white p-2 -mr-2 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-px bg-[#1a1a1a] mx-8 relative">
        <div
          className="absolute inset-y-0 left-0 bg-emerald-500/70 transition-all"
          style={{ width: `${pct}%` }}
          data-testid="onboarding-progress-bar"
        />
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3">
        {STEPS.map((s, idx) => {
          const isDone = steps[s.key];
          const isActive = idx === activeIdx;
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              data-testid={`onboarding-step-${s.key}`}
              data-state={isDone ? "done" : isActive ? "active" : "pending"}
              className={`relative p-6 border-r border-[#262626] last:border-r-0 ${
                isActive ? "bg-[#121212]" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 shrink-0 w-6 h-6 rounded-full border flex items-center justify-center ${
                    isDone
                      ? "bg-emerald-500 border-emerald-500 text-black"
                      : isActive
                      ? "border-white text-white"
                      : "border-[#3a3a3a] text-zinc-600"
                  }`}
                >
                  {isDone ? <Check size={13} strokeWidth={3} /> : <Circle size={6} className="fill-current" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Icon size={13} className={isDone ? "text-emerald-400" : "text-zinc-400"} strokeWidth={2} />
                    <div
                      className={`text-sm font-medium ${
                        isDone ? "text-zinc-500 line-through decoration-zinc-700" : "text-white"
                      }`}
                    >
                      {s.title}
                    </div>
                  </div>
                  <p className={`text-xs mt-2 leading-relaxed ${isDone ? "text-zinc-600" : "text-zinc-400"}`}>
                    {s.body}
                  </p>
                  {!isDone && (
                    <button
                      onClick={() => navigate(s.href)}
                      data-testid={`onboarding-cta-${s.key}`}
                      className={`mt-4 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-sm transition-colors ${
                        isActive
                          ? "bg-white text-black hover:bg-zinc-200"
                          : "border border-[#2a2a2a] text-zinc-300 hover:border-white hover:text-white"
                      }`}
                    >
                      {s.cta} <ArrowRight size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
